import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, Send, X, Loader2, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";

const SUGGESTIONS = [
  "I need a new illuminated shopfront sign",
  "How much is a vehicle wrap?",
  "Can you do window graphics for my cafe?",
];

const getSessionId = () => {
  let id = localStorage.getItem("sfm_chat_session");
  if (!id) {
    id = `sfm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("sfm_chat_session", id);
  }
  return id;
};

const Bubble = ({ role, content }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
    <div
      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
        role === "user" ? "bg-amber text-void" : "border border-white/10 bg-charcoal text-white/85"
      }`}
    >
      {content}
    </div>
  </div>
);

export const ChatAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm the Shop Front Makers assistant. Tell me about your signage or shopfront project and I'll help you scope it for a free quote." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, loading]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: content });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: formatApiErrorDetail(e.response?.data?.detail) || "Sorry, I couldn't respond just now. Please call us on 085 846 8599 or use the quote form." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-launcher"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close assistant" : "Open Instant Quote Assistant"}
        className="fixed bottom-6 left-6 z-40 flex h-14 items-center gap-2 rounded-full border border-amber/40 bg-void/90 px-5 text-amber shadow-lg backdrop-blur transition-transform hover:scale-105"
      >
        {open ? <X size={22} /> : <Sparkles size={20} />}
        {!open && <span className="font-mono text-xs uppercase tracking-widest">Ask AI</span>}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-24 left-4 right-4 z-40 flex h-[70vh] max-h-[560px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-obsidian shadow-2xl sm:left-6 sm:right-auto sm:w-[380px]"
          >
            <div className="flex items-center gap-3 border-b border-white/10 bg-charcoal px-5 py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber/15 text-amber">
                <Bot size={18} />
              </div>
              <div>
                <p className="font-display text-sm font-bold uppercase tracking-wide text-white">Quote Assistant</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Powered by AI · replies in seconds</p>
              </div>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto p-4">
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role} content={m.content} />
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-charcoal px-4 py-2.5 text-sm text-white/60">
                    <Loader2 className="animate-spin" size={14} /> Typing…
                  </div>
                </div>
              )}
              {messages.length === 1 && !loading && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid="chat-suggestion"
                      onClick={() => send(s)}
                      className="rounded-full border border-white/15 px-3 py-1.5 text-left text-xs text-white/70 transition-colors hover:border-amber hover:text-amber"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-white/10 bg-charcoal p-3"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Describe your project…"
                className="flex-1 rounded-full border border-white/10 bg-void px-4 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-amber"
              />
              <button
                type="submit"
                data-testid="chat-send"
                disabled={loading || !input.trim()}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-amber text-void transition-transform hover:scale-105 disabled:opacity-50"
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
